import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { HilfePost } from './hilfe.service';
import { MarketItem } from './marketplace.service';

interface SavedItemIdsResponse {
  adminNews: string[];
  vereinPost: string[];
  companyPost: string[];
  marketItem: string[];
  hilfePost: string[];
  communityEvent: string[];
}

@Injectable({ providedIn: 'root' })
export class SavedItemsService {
  private http = inject(HttpClient);
  private baseUrl = environment.apiUrl;

  private readonly savedAdminNews = signal<Set<string>>(new Set());
  private readonly savedVereinPost = signal<Set<string>>(new Set());
  private readonly savedCompanyPost = signal<Set<string>>(new Set());
  private readonly savedMarketItem = signal<Set<string>>(new Set());
  private readonly savedHilfePost = signal<Set<string>>(new Set());
  private readonly savedCommunityEvent = signal<Set<string>>(new Set());

  readonly savedMarketItemIds = computed(() => this.savedMarketItem());
  readonly savedHilfePostIds = computed(() => this.savedHilfePost());
  readonly savedFeedIds = computed(() => ({
    adminNews: this.savedAdminNews(),
    vereinPost: this.savedVereinPost(),
    companyPost: this.savedCompanyPost(),
    communityEvent: this.savedCommunityEvent(),
  }));

  loadSavedIds(): void {
    this.http.get<SavedItemIdsResponse>(`${this.baseUrl}/api/saved-items/ids`).subscribe({
      next: (ids) => {
        this.savedAdminNews.set(new Set(ids.adminNews));
        this.savedVereinPost.set(new Set(ids.vereinPost));
        this.savedCompanyPost.set(new Set(ids.companyPost));
        this.savedMarketItem.set(new Set(ids.marketItem));
        this.savedHilfePost.set(new Set(ids.hilfePost));
        this.savedCommunityEvent.set(new Set(ids.communityEvent ?? []));
      },
      error: () => {},
    });
  }

  isSaved(itemType: string, itemId: string): boolean {
    return this.setForType(itemType).has(itemId);
  }

  toggle(itemType: string, itemId: string): Observable<void> {
    if (this.isSaved(itemType, itemId)) {
      return this.unsave(itemType, itemId);
    }
    return this.save(itemType, itemId);
  }

  private save(itemType: string, itemId: string): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}/api/saved-items`, { itemType, itemId })
      .pipe(tap(() => this.addToSet(itemType, itemId)));
  }

  private unsave(itemType: string, itemId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/api/saved-items/${itemType}/${itemId}`)
      .pipe(tap(() => this.removeFromSet(itemType, itemId)));
  }

  private setForType(itemType: string): Set<string> {
    switch (itemType) {
      case 'admin_news':
        return this.savedAdminNews();
      case 'verein_post':
        return this.savedVereinPost();
      case 'company_post':
        return this.savedCompanyPost();
      case 'market_item':
        return this.savedMarketItem();
      case 'hilfe_post':
        return this.savedHilfePost();
      case 'community_event':
        return this.savedCommunityEvent();
      default:
        return new Set();
    }
  }

  private addToSet(itemType: string, itemId: string): void {
    const update = (s: Set<string>) => new Set([...s, itemId]);
    switch (itemType) {
      case 'admin_news':
        this.savedAdminNews.update(update);
        break;
      case 'verein_post':
        this.savedVereinPost.update(update);
        break;
      case 'company_post':
        this.savedCompanyPost.update(update);
        break;
      case 'market_item':
        this.savedMarketItem.update(update);
        break;
      case 'hilfe_post':
        this.savedHilfePost.update(update);
        break;
      case 'community_event':
        this.savedCommunityEvent.update(update);
        break;
    }
  }

  private removeFromSet(itemType: string, itemId: string): void {
    const update = (s: Set<string>) => {
      const n = new Set(s);
      n.delete(itemId);
      return n;
    };
    switch (itemType) {
      case 'admin_news':
        this.savedAdminNews.update(update);
        break;
      case 'verein_post':
        this.savedVereinPost.update(update);
        break;
      case 'company_post':
        this.savedCompanyPost.update(update);
        break;
      case 'market_item':
        this.savedMarketItem.update(update);
        break;
      case 'hilfe_post':
        this.savedHilfePost.update(update);
        break;
      case 'community_event':
        this.savedCommunityEvent.update(update);
        break;
    }
  }

  getSavedHilfePosts(search = ''): Observable<HilfePost[]> {
    return this.http.get<HilfePost[]>(`${this.baseUrl}/api/hilfe/saved`, {
      params: this.searchParams(search),
    });
  }

  getBookmarkedMarketItems(search = ''): Observable<MarketItem[]> {
    return this.http.get<MarketItem[]>(`${this.baseUrl}/api/marketplace/bookmarks`, {
      params: this.searchParams(search),
    });
  }

  private searchParams(search: string): Record<string, string> {
    const text = search.trim();
    return text ? { q: text } : {};
  }
}
